import React from "react";
import styled from "styled-components";
import { PreviewInput } from "../../atoms/PreviewInput";
import { BlogDetailResponse } from "../../../core/api/requests/blog/detail/types";

type Props = {
  blog: BlogDetailResponse;
};

export const DetailBlogForm: React.FC<Props> = ({ blog }) => {
  const [imageError, setImageError] = React.useState(false);

  // Helper function to format date in a user-friendly way
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return "Invalid Date";
    }
  };

  // Helper function to build image URL that works in both dev and production
  const buildImageUrl = (imagePath: string): string => {
    if (!imagePath) return '';

    // If the image path is already a complete URL, use it directly
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // Priority 1: Use dedicated media URL environment variable if available
    if (process.env.REACT_APP_MEDIA_URL) {
      const mediaUrl = process.env.REACT_APP_MEDIA_URL;
      return `${mediaUrl}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
    }

    // Priority 2: Use API base URL and construct media URL (development fallback)
    const apiBaseUrl = process.env.REACT_APP_API_BASE || '';
    if (apiBaseUrl) {
      try {
        const url = new URL(apiBaseUrl);
        const mediaBaseUrl = `${url.protocol}//${url.host}`;
        return `${mediaBaseUrl}${imagePath}`;
      } catch (error) {
        console.warn('Invalid API base URL, falling back to relative path');
      }
    }

    // Priority 3: Fallback to relative path (works if frontend and media are on same domain)
    return imagePath;
  };

  const imageUrl = blog.image ? buildImageUrl(blog.image) : '';

  return (
    <>
      <StyledFormGrid>
        <PreviewInput
          label="Title"
          name="title"
          value={blog.title}
          fullWidth
        />
        <PreviewInput
          label="Author Name"
          name="author_name"
          value={blog.author_name || "N/A"}
          fullWidth
        />
        <PreviewInput
          label="Publish Status"
          name="publish"
          value={blog.publish ? "Published" : "Draft"}
          fullWidth
        />
        <PreviewInput
          label="Created At"
          name="created_at"
          value={formatDate(blog.created_at)}
          fullWidth
        />
        <StyledImageContainer>
          <StyledImageLabel>Image</StyledImageLabel>
          {blog.image && !imageError ? (
            <StyledImage
              src={imageUrl}
              alt="Blog"
              onError={() => {
                console.error('Image failed to load:', imageUrl);
                setImageError(true);
              }}
            />
          ) : blog.image && imageError ? (
            <StyledImageFallback>
              <StyledImageText>Image: {blog.image}</StyledImageText>
              <StyledImageUrl>URL: {imageUrl}</StyledImageUrl>
            </StyledImageFallback>
          ) : (
            <StyledNoImage>No image</StyledNoImage>
          )}
        </StyledImageContainer>
      </StyledFormGrid>
      <StyledContentSection>
        <StyledContentContainer>
          <StyledContentLabel>Content</StyledContentLabel>
          <StyledContentValue
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </StyledContentContainer>
      </StyledContentSection>
    </>
  );
};

const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StyledContentSection = styled.div`
  margin-top: 16px;
`;

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const StyledContentLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
`;

const StyledContentValue = styled.div`
  padding: 17px 16px;
  border-radius: 6px;
  border: 1px solid #cccccc;
  background-color: #f9f9f9;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1.5;
  color: #333333;
  min-height: 120px;
  word-wrap: break-word;

  h1, h2, h3, h4, h5, h6 {
    margin: 0 0 16px 0;
    font-weight: 600;
  }

  p {
    margin: 0 0 16px 0;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 8px 0;
  }
`;

const StyledImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const StyledImageLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
`;

const StyledImage = styled.img`
  width: 100%;
  max-width: 300px;
  height: auto;
  border-radius: 6px;
  border: 1px solid #cccccc;
  object-fit: cover;
`;

const StyledNoImage = styled.div`
  padding: 17px 16px;
  border-radius: 6px;
  border: 1px solid #cccccc;
  background-color: #f9f9f9;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 100%;
  color: #666666;
  font-style: italic;
`;

const StyledImageFallback = styled.div`
  padding: 17px 16px;
  border-radius: 6px;
  border: 1px solid #cccccc;
  background-color: #f9f9f9;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 100%;
  color: #666666;
  font-style: italic;
`;

const StyledImageText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: "🖼️";
    font-size: 16px;
  }
`;

const StyledImageUrl = styled.div`
  font-size: 12px;
  color: #999999;
  margin-top: 4px;
  word-break: break-all;
`; 